/**
 * The headline IS the product: dreambooth.app/<something>.
 *
 * Every AI builder opens with "describe it and we'll generate it". The thing
 * only Dreambooth can say is that the result lives at a short URL you can read
 * out loud at a party — so the URL is the H1, not a feature bullet.
 *
 * It cycles through real live booths (in step with the hero device) until the
 * visitor clicks it, at which point it becomes an input and whatever they type
 * is carried into the Name & link step as their slug.
 */

import React, { useEffect, useRef, useState } from 'react'
import { slugify } from '../onboardingStore'
import { useCopy } from '../regionCopy'
import { publicBoothHostLabel } from '../publicOrigin'

interface UrlHeadlineProps {
  /** Slug currently on show, when nothing has been typed. */
  rotatingSlug: string | null
  /** The visitor's own slug, once they've claimed one. */
  claimedSlug: string
  onClaim: (slug: string) => void
  /** Rotation must stop while they're typing their own. */
  onEditingChange: (editing: boolean) => void
}

export const UrlHeadline: React.FC<UrlHeadlineProps> = ({
  rotatingSlug,
  claimedSlug,
  onClaim,
  onEditingChange
}) => {
  const [editing, setEditing] = useState(false)
  const c = useCopy()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    onEditingChange(editing || claimedSlug.length > 0)
  }, [editing, claimedSlug, onEditingChange])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const shown = claimedSlug || rotatingSlug || 'your-party'
  const measure = `${shown || 'your-party'}`

  return (
    <h1 className="text-[1.85rem] font-extrabold leading-[1.08] tracking-[-0.035em] sm:text-[2.6rem]">
      <span className="text-whiteout/40">{publicBoothHostLabel()}/</span>
      {editing ? (
        <span className="relative inline-grid">
          {/* Invisible twin sizes the input to its content — an input can't
              shrink-to-fit on its own, and a fixed width would either clip a
              long slug or leave a gap after a short one. */}
          <span aria-hidden className="invisible col-start-1 row-start-1 whitespace-pre pr-px">
            {measure || ' '}
          </span>
          <input
            ref={inputRef}
            value={claimedSlug}
            onChange={(e) => onClaim(slugify(e.target.value))}
            onBlur={() => setEditing(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Escape') setEditing(false)
            }}
            maxLength={40}
            aria-label={c.yourBoothLink}
            placeholder={c.slugPlaceholder}
            className="col-start-1 row-start-1 w-full border-b-2 border-blueSparkle bg-transparent text-whiteout placeholder-whiteout/25 outline-none"
          />
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          title={c.claimThisLink}
          className="border-b-2 border-blueSparkle text-whiteout transition-colors hover:border-purpleSnail"
        >
          {shown}
        </button>
      )}
    </h1>
  )
}

export default UrlHeadline
